import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MultiSelectFilterProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  /** Optional custom colors for portal branding */
  primaryColor?: string;
  secondaryColor?: string;
  /** Prevents deselecting the last item (for organization filters) */
  requireAtLeastOne?: boolean;
  /** Show Select All / Clear buttons in dropdown */
  showSelectAllControls?: boolean;
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  className,
  primaryColor,
  secondaryColor,
  requireAtLeastOne,
  showSelectAllControls,
}) => {
  const [open, setOpen] = useState(false);
  const hasSelection = selectedValues.length > 0;
  const allSelected = selectedValues.length === options.length && options.length > 0;

  // Track pointer movement to distinguish scrolls from taps on mobile
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerMovedRef = useRef(false);
  const MOVEMENT_THRESHOLD = 10; // pixels

  const handleOpenChange = (newOpen: boolean) => {
    // Only allow closing through Radix's handler (e.g., clicking outside, pressing Escape)
    // Opening is handled manually via onClick to prevent scroll-triggered opens
    if (!newOpen) {
      setOpen(false);
    }
  };

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      // Prevent removing last item if required
      if (requireAtLeastOne && selectedValues.length === 1) {
        return;
      }
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectAll = () => onChange(options.map((o) => o.value));

  const clearAll = () => {
    if (requireAtLeastOne) {
      // Keep first option selected
      onChange([options[0]?.value].filter(Boolean));
    } else {
      onChange([]);
    }
  };

  // Use custom colors if provided (for customer portal), otherwise use default styling
  const useCustomColors = primaryColor && secondaryColor;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between transition-all duration-200",
            !useCustomColors && hasSelection && "border-primary border-2 bg-primary/5",
            className
          )}
          style={
            useCustomColors
              ? {
                  borderColor: hasSelection ? secondaryColor : undefined,
                  borderWidth: hasSelection ? "2px" : "1px",
                  backgroundColor: hasSelection ? secondaryColor + "10" : undefined,
                }
              : undefined
          }
          onMouseEnter={(e) => {
            if (useCustomColors) {
              e.currentTarget.style.borderColor = secondaryColor!;
              e.currentTarget.style.borderWidth = "2px";
            }
          }}
          onMouseLeave={(e) => {
            if (useCustomColors) {
              e.currentTarget.style.borderColor = hasSelection ? secondaryColor! : "";
              e.currentTarget.style.borderWidth = hasSelection ? "2px" : "1px";
            }
          }}
          onPointerDownCapture={(e) => {
            // Intercept pointerdown before Radix gets it to prevent auto-open on touch
            pointerStartRef.current = { x: e.clientX, y: e.clientY };
            pointerMovedRef.current = false;
            e.stopPropagation();
          }}
          onPointerMove={(e) => {
            // Track if user is scrolling (moved finger significantly)
            if (pointerStartRef.current) {
              const dx = Math.abs(e.clientX - pointerStartRef.current.x);
              const dy = Math.abs(e.clientY - pointerStartRef.current.y);
              if (dx > MOVEMENT_THRESHOLD || dy > MOVEMENT_THRESHOLD) {
                pointerMovedRef.current = true;
              }
            }
          }}
          onPointerUp={() => {
            pointerStartRef.current = null;
          }}
          onPointerCancel={() => {
            pointerStartRef.current = null;
            pointerMovedRef.current = false;
          }}
          onClick={() => {
            // Only toggle if user didn't scroll (onClick fires after successful tap)
            if (!pointerMovedRef.current) {
              setOpen((prev) => !prev);
            }
            pointerMovedRef.current = false;
          }}
        >
          <div className="flex items-center">
            <Filter
              className={cn(
                "mr-2 h-4 w-4",
                !useCustomColors && (hasSelection ? "text-primary" : "text-muted-foreground")
              )}
              style={
                useCustomColors
                  ? { color: hasSelection ? secondaryColor : primaryColor }
                  : undefined
              }
            />
            <span>
              {allSelected ? `All ${label}` : label}
              {hasSelection && !allSelected && ` (${selectedValues.length})`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[200px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {showSelectAllControls && (
          <>
            <div className="flex items-center justify-between px-2 py-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  selectAll();
                }}
                disabled={allSelected}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
                disabled={requireAtLeastOne && selectedValues.length <= 1}
              >
                Clear
              </Button>
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selectedValues.includes(option.value)}
            onCheckedChange={() => toggleValue(option.value)}
            onSelect={(e) => e.preventDefault()}
            disabled={requireAtLeastOne && selectedValues.length === 1 && selectedValues.includes(option.value)}
            className={cn(
              "cursor-pointer",
              requireAtLeastOne && selectedValues.length === 1 && selectedValues.includes(option.value) && "opacity-50 cursor-not-allowed"
            )}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
